import { Observable } from "rxjs";
import { INTEGRATIONS } from "constants/action-types";
import siteConfig from "site-config";
import { formatMessage } from "lib/globalize-util";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import { add as snackbarAdd } from "actions/snackbars";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import { MasterSchema, BrandNuORDERMapSchema } from "schemas/schemas";
import { updateEntities } from "redux-query";

export const fetchNuOrderMasterSchema = (action$, store) => {
    return action$
        .ofType(INTEGRATIONS.MASTER_SCHEMA.FETCH.START)
        .map((action) => {
            return action.payload;
        })
        .concatMap(() => {
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/master-schema`,
            })
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const records = getRecordsFromSchema(MasterSchema);
                        const { result, entities } = normalizeData(response, MasterSchema, records);
                        return { error: false, entities, result };
                    }
                })
                .flatMap(({ error, entities, result }: any) => {
                    if (!error) {
                        return [
                            {
                                type: INTEGRATIONS.MASTER_SCHEMA.FETCH.COMPLETED,
                                payload: [result],
                            },
                            updateEntities({
                                masterSchemas: () => {
                                    return {
                                        ...entities.masterSchema,
                                    };
                                },
                            }),
                        ];
                    } else {
                        return [
                            {
                                error: true,
                                payload: error,
                                type: INTEGRATIONS.MASTER_SCHEMA.FETCH.COMPLETED,
                            },
                            snackbarAdd({ message: error.message }),
                        ];
                    }
                })
                .catch((error) => {
                    // 0 is a timeout for rxjs
                    if (error.status === 0) {
                        error.message = formatMessage("Something went wrong, please refresh and try again");
                    }
                    return Observable.merge(
                        Observable.of(snackbarAdd({ message: error.message })),
                        Observable.of({
                            error: true,
                            payload: error,
                            type: INTEGRATIONS.MASTER_SCHEMA.FETCH.COMPLETED,
                        }),
                    );
                });
        });
};

export const createBrandSchemaMap = (action$, store) => {
    return action$
        .ofType(INTEGRATIONS.BRAND_SCHEMA_MAP.CREATE.START)
        .map((action) => {
            return action.payload;
        })
        .concatMap(() => {
            const { scope } = store.getState();
            const brandId = scope.brand._id;
            return ajax({
                method: "POST",
                url: `${siteConfig.api}/brand/${brandId}/map`,
                body: {
                    fields: [],
                },
                handle401: true,
            })
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const records = getRecordsFromSchema(BrandNuORDERMapSchema);
                        const { result, entities } = normalizeData(response, BrandNuORDERMapSchema, records);
                        return { error: false, entities, result };
                    }
                })
                .flatMap(({ error, entities, result }: any) => {
                    if (!error) {
                        return [
                            {
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.CREATE.COMPLETED,
                                payload: [result],
                            },
                            updateEntities({
                                brandSchemaMap: () => {
                                    return {
                                        ...entities.brandSchemaMap,
                                    };
                                },
                            }),
                        ];
                    } else {
                        return [
                            {
                                error: true,
                                payload: error,
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.CREATE.COMPLETED,
                            },
                            snackbarAdd({ message: error.message }),
                        ];
                    }
                })
                .catch((error) => {
                    if (error.xhr && error.xhr.response) {
                        error.message = error.xhr.response.message;
                    }
                    // 0 is a timeout for rxjs
                    if (error.status === 0) {
                        error.message = formatMessage("Something went wrong, please refresh and try again");
                    }
                    if (error.status === 401 && error.message === "Invalid permissions") {
                        return Observable.merge(
                            Observable.of(
                                snackbarAdd({
                                    message: formatMessage(
                                        "You do not have the correct permissions to update this mapping.",
                                    ),
                                }),
                            ),
                            Observable.of({
                                payload: [],
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.UPDATE.COMPLETED,
                            }),
                        );
                    } else {
                        return Observable.merge(
                            Observable.of({
                                error: true,
                                payload: error,
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.CREATE.COMPLETED,
                            }),
                        );
                    }
                });
        });
};

export const getBrandSchemaMap = (action$, store) => {
    return action$
        .ofType(INTEGRATIONS.BRAND_SCHEMA_MAP.FETCH.START)
        .map((action) => {
            return action.payload;
        })
        .concatMap((brandId: string) => {
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/brand/${brandId}/map`,
            })
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const records = getRecordsFromSchema(BrandNuORDERMapSchema);
                        const { result, entities } = normalizeData(response, BrandNuORDERMapSchema, records);
                        return { error: false, entities, result };
                    }
                })
                .flatMap(({ error, entities, result }: any) => {
                    if (!error) {
                        return [
                            {
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.FETCH.COMPLETED,
                                payload: [result],
                            },
                            updateEntities({
                                brandSchemaMap: () => {
                                    return {
                                        ...entities.brandSchemaMap,
                                    };
                                },
                            }),
                        ];
                    } else {
                        return [
                            {
                                error: true,
                                payload: error,
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.FETCH.COMPLETED,
                            },
                            snackbarAdd({ message: error.message }),
                        ];
                    }
                })
                .catch((error) => {
                    if (error.xhr && error.xhr.response) {
                        error.message = error.xhr.response.message;
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: INTEGRATIONS.BRAND_SCHEMA_MAP.FETCH.COMPLETED,
                        }),
                    );
                });
        });
};

export const updateBrandSchemaMap = (action$, store) => {
    return action$
        .ofType(INTEGRATIONS.BRAND_SCHEMA_MAP.UPDATE.START)
        .map((action) => {
            return action.payload;
        })
        .concatMap((payload) => {
            const { scope } = store.getState();
            const brandId = scope.brand._id;
            payload.fields = payload.fields.filter((field) => !!field && field.brand_key); // prevent nulls
            return ajax({
                method: "PATCH",
                url: `${siteConfig.api}/brand/${brandId}/map`,
                body: payload,
                handle401: true,
            })
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const records = getRecordsFromSchema(BrandNuORDERMapSchema);
                        const { result, entities } = normalizeData(response, BrandNuORDERMapSchema, records);
                        return { error: false, entities, result };
                    }
                })
                .flatMap(({ error, entities, result }: any) => {
                    if (!error) {
                        return [
                            {
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.UPDATE.COMPLETED,
                                payload: [result],
                            },
                            snackbarAdd({ message: formatMessage("Brand Map updated") }),
                            updateEntities({
                                brandSchemaMap: () => {
                                    return {
                                        ...entities.brandSchemaMap,
                                    };
                                },
                            }),
                        ];
                    } else {
                        return [
                            {
                                error: true,
                                payload: error,
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.UPDATE.COMPLETED,
                            },
                            snackbarAdd({ message: error.message }),
                        ];
                    }
                })
                .catch((error) => {
                    if (error.xhr && error.xhr.response) {
                        error.message = error.xhr.response.message;
                    }

                    if (error.status === 404) {
                        error.message = formatMessage(
                            "This brand mapping was deleted by another user or in another session",
                        );
                    }
                    // 0 is a timeout for rxjs
                    if (error.status === 0) {
                        error.message = formatMessage("Something went wrong, please refresh and try again");
                    }

                    if (error.status === 401 && error.message === "Invalid permissions") {
                        return Observable.merge(
                            Observable.of(
                                snackbarAdd({
                                    message: formatMessage(
                                        "You do not have the correct permissions to update this mapping.",
                                    ),
                                }),
                            ),
                            Observable.of({
                                payload: payload.brand_schema,
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.UPDATE.COMPLETED,
                            }),
                        );
                    } else {
                        return Observable.merge(
                            Observable.of(snackbarAdd({ message: error.message })),
                            Observable.of({
                                error: true,
                                payload: error,
                                type: INTEGRATIONS.BRAND_SCHEMA_MAP.UPDATE.COMPLETED,
                            }),
                        );
                    }
                });
        });
};
